import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Form, Select, DatePicker, Checkbox, Input, Button } from 'antd';

import {
  ContactStore,
  AppStore,
  ShopStore,
  CarStore,
  CurrentUserStore,
} from 'stores';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import UserStore from 'stores/UserStore';
import { PlusOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const RecipientForm = ({
  showAppointmentTime = true,
  initialValue = {
    car: {},
    startTimeAppointment: null,
    assignedTo: {},
    attachReport: false,
    description: '',
  },
  setCallables = () => {},
  onChangeValue = () => {},
}) => {
  const [form] = Form.useForm();
  const [cars, setCars] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [savingContact, setSavingContact] = useState(false);

  const CONTACT_FORM_MODAL_ID = 'contact_form_modal_id';

  const fetchCars = async () => {
    const shopId = ShopStore.currentShop?.id || CurrentUserStore.user?.shopId;
    if (!shopId) return;

    try {
      const carsList = await CarStore.getShopCarsBasicInfoAndReturn(shopId);
      setCars(carsList || []);
    } catch (error) {
      AppStore.addError('Error loading vehicles: ' + error.message);
    }
  };

  useEffect(() => {
    setCallables({
      handleSubmit: handleSubmit,
      resetForm: () => form.resetFields(),
    });

    fetchCars();
    fetchShopContacts();
  }, []);

  useEffect(() => {
    if (
      initialValue.car &&
      initialValue.car.id &&
      cars.some((c) => c.id === initialValue.car.id)
    ) {
      form.setFieldsValue({ vehicle: initialValue.car.id });
    }
  }, [cars, initialValue.car?.id, form]);

  const fetchShopContacts = async () => {
    try {
      const contactsList = await ContactStore.getShopContacts(
        ShopStore.currentShop.id
      );
      const usersList = await UserStore.fetchShopUsers(
        ShopStore.currentShop.id
      );

      const contacts = (contactsList || []).map((c) => ({
        ...c,
        source: 'vendor',
      }));

      const users = (usersList || []).map((u) => ({
        ...u,
        id: u.userId,
        source: 'user',
        name: `${u.firstName} ${u.lastName}`,
      }));

      setContacts([...contacts, ...users]);
    } catch (error) {
      AppStore.addError('Error loading contacts: ' + error.message);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    return values;
  };

  const onSubmitContactForm = async (data) => {
    try {
      setSavingContact(true);
      const newContact = await ContactStore.createShopContact({
        ...data,
        shopId: ShopStore.currentShop.id,
      });
      AppStore.addSuccess('Contact saved successfully');

      const newContactId =
        newContact.id || (newContact.data && newContact.data.id);
      const newContactObj = JSON.stringify({
        id: newContactId,
        source: 'vendor',
      });
      setContacts((prev) => {
        if (!prev.some((c) => c.id === newContactId)) {
          return [
            ...prev,
            { ...newContact, id: newContactId, source: 'vendor' },
          ];
        }
        return prev;
      });

      const currentSelected = form.getFieldValue('assignedTo') || [];
      const newSelected = currentSelected.includes(newContactObj)
        ? currentSelected
        : [...currentSelected, newContactObj];
      form.setFieldsValue({ assignedTo: newSelected });
      onChangeValue('assignedTo', newSelected);
      await fetchShopContacts();

      AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
    } catch (error) {
      AppStore.addError('Error to save contact: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  const showModalToAddNewContact = () => {
    AppStore.openModals.set(CONTACT_FORM_MODAL_ID, true);
  };

  const carOptions = cars.map((car) => {
    return (
      <Option key={car.id} value={car.id}>
        {`${car.unitId} - ${car.vin}`}
      </Option>
    );
  });

  const contactOptions = contacts.map((contact) => (
    <Option
      key={contact.source + contact.id}
      value={JSON.stringify({ id: contact.id, source: contact.source })}
    >
      {`${contact.name}`}
    </Option>
  ));

  if (
    initialValue.car &&
    initialValue.car.id &&
    !cars.some((c) => c.id === initialValue.car.id)
  ) {
    return null;
  }

  return (
    <>
      <Form
        form={form}
        initialValues={{
          vehicle: initialValue.car?.id,
          startTimeAppointment: initialValue.startTimeAppointment,
          assignedTo: initialValue.assignedTo?.id,
          attachReport: initialValue.attachReport,
          description: initialValue.description,
        }}
        layout="vertical"
      >
        <Form.Item
          label="Select Vehicle"
          name="vehicle"
          rules={[
            {
              required: true,
              message: 'Please select the vehicle!',
            },
          ]}
        >
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select a vehicle"
            optionFilterProp="children"
            onChange={(value) => onChangeValue('vehicle', value)}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {carOptions}
          </Select>
        </Form.Item>

        {showAppointmentTime && (
          <Form.Item
            label="Appointment Time"
            name="startTimeAppointment"
            rules={[
              {
                required: true,
                message: 'Please select the appointment time.',
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              onChange={(value) => onChangeValue('startTimeAppointment', value)}
              showTime
            />
          </Form.Item>
        )}

        <Form.Item
          label="Assigned To"
          name="assignedTo"
          rules={[
            {
              required: true,
              message: 'Please select a contact.',
            },
          ]}
        >
          <Select
            showSearch
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select a contact"
            optionFilterProp="children"
            value={form.getFieldValue('assignedTo') || []}
            onChange={(value) => {
              form.setFieldsValue({ assignedTo: value });
              onChangeValue('assignedTo', value);
            }}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {contactOptions}
          </Select>
        </Form.Item>

        <Button icon={<PlusOutlined />} onClick={showModalToAddNewContact}>
          New Contact
        </Button>

        <Form.Item
          name="attachReport"
          valuePropName="checked"
          style={{ marginTop: '20px' }}
        >
          <Checkbox
            checked={initialValue.attachReport}
            onChange={(e) => onChangeValue('attachReport', e.target.checked)}
          >
            Attach Vehicle Health Report as PDF in the email
          </Checkbox>
        </Form.Item>

        <Form.Item label="Aditional Information" name="description">
          <>
            <TextArea
              rows={4}
              style={{ marginTop: 16 }}
              onChange={(e) => onChangeValue('description', e.target.value)}
            />
            <span>
              This will be automatically added in the body of the email with the
              appointment details.
            </span>
          </>
        </Form.Item>
      </Form>

      <ContactFormModal
        modalId={CONTACT_FORM_MODAL_ID}
        saving={savingContact}
        title={'Add Vendor'}
        defaultData={{}}
        onCancel={() => {
          AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
        }}
        onSubmit={onSubmitContactForm}
      />
    </>
  );
};

RecipientForm.propTypes = {
  showAppointmentTime: PropTypes.bool,
  initialValue: PropTypes.object,
  setCallables: PropTypes.func,
  onChangeValue: PropTypes.func,
};

export default RecipientForm;
